import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Table,
  Stack,
  Chip,
  Button,
  IconButton,
  Popover,
  MenuItem,
  TableBody,
  TableCell,
  TableRow,
  Container,
  Typography,
  TableContainer,
  Switch,
  Tabs,
  Tab,
  Box,
  TextField,
  Avatar,
  Divider,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Autocomplete,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  collection,
  getDocs,
  getFirestore,
  deleteDoc,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
} from 'firebase/firestore';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { TableSkeleton } from '../components/loading';
import firebaseApp from '../utils/firebaseConfig';
import { matchesSearch } from '../utils/search';
import { isPrivateCourse, COURSE_LANGUAGE_OPTIONS, LICENSE_TYPE_OPTIONS, privateCourseSummary } from '../utils/privateCourse';
import { openAttendanceSheet } from '../sections/@dashboard/courses/attendanceSheet';
import { partnerRentalReason } from '../utils/partners';
import { openCertificates } from '../sections/@dashboard/courses/certificateSheet';

const db = getFirestore(firebaseApp);

const DAYS_FULL = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}

function startOfWeek(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay()); // domingo
  return d;
}

// Al entrar a un asistente y dar "atrás", la página debe quedar DONDE ESTABA
// (mismo día expandido, misma posición) — pedido del cliente 2026-08-18. El
// estado de la vista se guarda al navegar y se restaura al volver (expira a
// la hora para no arrastrar una vista vieja al otro día).
const COURSES_VIEW_KEY = 'coursesViewState';

function readSavedCoursesView() {
  try {
    const raw = sessionStorage.getItem(COURSES_VIEW_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (Date.now() - (v.at || 0) > 60 * 60 * 1000) return null;
    return v;
  } catch (e) {
    return null;
  }
}

export default function CoursesPage() {
  const [tab, setTab] = useState('calendar');
  const [open, setOpen] = useState(null);
  const [courses, setCourses] = useState([]);
  const [row, setRow] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const savedView = useRef(readSavedCoursesView()).current;

  // Calendar tab state
  const [weekStart, setWeekStart] = useState(savedView?.weekStart
    ? startOfWeek(new Date(`${savedView.weekStart}T00:00:00`))
    : startOfWeek(new Date()));
  const [filterCourseId, setFilterCourseId] = useState(savedView?.filterCourseId || '');
  const [reservations, setReservations] = useState([]);
  const [scheduleRules, setScheduleRules] = useState([]); // availability_rules
  const [calLoading, setCalLoading] = useState(false);
  // Vista semana completa o un día específico
  const [calView, setCalView] = useState(savedView?.calView || 'week');
  const [dayFilter, setDayFilter] = useState(savedView?.dayFilter || toISODate(new Date()));
  // Días expandidos (lista de asistentes visible); por defecto colapsados
  const [expandedDays, setExpandedDays] = useState(savedView?.expandedDays || {});
  const toggleDayExpanded = (iso) => setExpandedDays((prev) => ({ ...prev, [iso]: !prev[iso] }));

  // Guardar la vista justo antes de entrar al detalle de un asistente
  const goToAttendee = (attendeeId) => {
    try {
      sessionStorage.setItem(COURSES_VIEW_KEY, JSON.stringify({
        at: Date.now(),
        weekStart: toISODate(weekStart),
        calView,
        dayFilter,
        expandedDays,
        filterCourseId,
        scrollY: window.scrollY,
      }));
    } catch (e) { /* ignore */ }
    navigate(`/dashboard/reservations/details?id=${attendeeId}`);
  };

  // Restaurar el scroll cuando ya cargaron las reservaciones
  const scrollRestored = useRef(false);
  useEffect(() => {
    if (savedView?.scrollY != null && !scrollRestored.current && reservations.length > 0) {
      scrollRestored.current = true;
      setTimeout(() => window.scrollTo(0, savedView.scrollY), 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservations]);

  // Add attendee dialog
  const [members, setMembers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addSlot, setAddSlot] = useState(null); // { categoryId, categoryName, date, startTime, endTime }
  const [addForm, setAddForm] = useState({
    customer: null,
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerLicense: '',
    notes: '',
  });
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState(null);

  // Print dialog: selección de instructores antes de imprimir la hoja
  const [employees, setEmployees] = useState([]);
  const [printOpen, setPrintOpen] = useState(false);
  const [printSlot, setPrintSlot] = useState(null); // { slot, dayDate }
  const [printChecked, setPrintChecked] = useState({}); // { [employeeId]: true }
  const [printSaving, setPrintSaving] = useState(false);
  // Asistentes sin requisitos del curso — bloquean la impresión
  // El diálogo de instructores sirve para la hoja de asistencia y los certificados
  const [printMode, setPrintMode] = useState('sheet'); // 'sheet' | 'certs'

  const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';

  useEffect(() => { fetchCourses(); fetchMembers(); fetchEmployees(); }, []);

  const fetchMembers = async () => {
    try {
      const snap = await getDocs(collection(db, 'members'));
      setMembers(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    } catch (e) { console.error(e); }
  };

  const fetchEmployees = async () => {
    try {
      const snap = await getDocs(collection(db, 'employees'));
      const list = snap.docs
        .map((d) => ({ ...d.data(), id: d.id }))
        .filter((e) => e.active !== false && (e.name || '').trim())
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      // SOLO los empleados con rol "Instructor" salen en el diálogo y en los PDFs
      const instructors = list.filter((e) =>
        Array.isArray(e.roles) && e.roles.some((rl) => /instructor/i.test(rl)));
      setEmployees(instructors);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (tab === 'calendar' && courses.length > 0) fetchCalendarReservations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, weekStart, filterCourseId, courses]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'reservation_categories'), where('type', '==', 'course'));
      const snap = await getDocs(q);
      setCourses(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchCalendarReservations = async () => {
    setCalLoading(true);
    try {
      const start = new Date(weekStart);
      const end = new Date(weekStart);
      end.setDate(end.getDate() + 6);
      const startISO = toISODate(start);
      const endISO = toISODate(end);

      // Reservaciones existentes
      const q = query(collection(db, 'reservations'), where('categoryType', '==', 'course'));
      const snap = await getDocs(q);
      let list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => r.date >= startISO && r.date <= endISO)
        .filter((r) => ['confirmed', 'completed', 'pending_payment', 'no_show'].includes(r.status));
      if (filterCourseId) list = list.filter((r) => r.categoryId === filterCourseId);
      setReservations(list);

      // Horarios programados (availability_rules) para los cursos
      const courseIds = filterCourseId
        ? [filterCourseId]
        : courses.map((c) => c.id);
      let rules = [];
      if (courseIds.length > 0) {
        // Firestore 'in' acepta hasta 30 elementos; chunk si hay más
        const chunks = [];
        for (let i = 0; i < courseIds.length; i += 30) {
          chunks.push(courseIds.slice(i, i + 30));
        }
        const ruleSnaps = await Promise.all(
          chunks.map((chunk) =>
            getDocs(query(collection(db, 'availability_rules'), where('categoryId', 'in', chunk)))
          )
        );
        rules = ruleSnaps.flatMap((s) => s.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
      setScheduleRules(rules);
    } catch (e) {
      console.error('Error loading calendar:', e);
    }
    setCalLoading(false);
  };

  const handleOpenMenu = (event, r) => { setOpen(event.currentTarget); setRow(r); };
  const handleCloseMenu = () => setOpen(null);

  const handleEdit = () => {
    navigate(`/dashboard/reservation-categories/information?id=${row.id}`);
    handleCloseMenu();
  };

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar curso "${row.name}"?`)) return;
    try {
      await deleteDoc(doc(db, 'reservation_categories', row.id));
      setCourses(courses.filter((c) => c.id !== row.id));
    } catch (e) { alert('Error eliminando curso'); }
    handleCloseMenu();
  };

  const handleToggleActive = async (item) => {
    await updateDoc(doc(db, 'reservation_categories', item.id), { active: !item.active });
    setCourses(courses.map((c) => (c.id === item.id ? { ...c, active: !c.active } : c)));
  };

  const openAddAttendee = (slot, dayDate) => {
    setAddSlot({
      categoryId: slot.categoryId,
      categoryName: slot.categoryName,
      date: toISODate(dayDate),
      startTime: slot.startTime,
      endTime: slot.endTime,
    });
    // Link de pago (2026-10-07): en cursos privados viene encendido; en los
    // regulares el staff lo prende si el cliente va a pagar por el link.
    const slotIsPrivate = isPrivateCourse(courses.find((c) => c.id === slot.categoryId) || { name: slot.categoryName, type: 'course' });
    setAddForm({
      customer: null,
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      customerLicense: '',
      notes: '',
      courseLanguage: '',
      licenseType: '',
      sendPaymentLink: slotIsPrivate,
    });
    setAddError(null);
    setAddOpen(true);
  };

  const closeAddAttendee = () => { setAddOpen(false); setAddSlot(null); };

  const handleAddAttendee = async () => {
    if (!addSlot) return;
    if (!addForm.customer && !addForm.customerName.trim() && !addForm.customerEmail.trim() && !addForm.customerPhone.trim()) {
      setAddError('Selecciona un cliente o ingresa al menos su nombre/email/teléfono.');
      return;
    }
    // Curso privado: idioma y nueva/renovación son obligatorios
    const addIsPrivate = isPrivateCourse(courses.find((c) => c.id === addSlot.categoryId) || { name: addSlot.categoryName, type: 'course' });
    if (addIsPrivate && (!addForm.courseLanguage || !addForm.licenseType)) {
      setAddError('Para un curso privado indica el idioma (Español/Inglés) y si es licencia nueva o renovación.');
      return;
    }
    setAddSaving(true);
    setAddError(null);
    try {
      const payload = {
        categoryId: addSlot.categoryId,
        date: addSlot.date,
        startTime: addSlot.startTime,
        customerName: addForm.customer?.fullName || addForm.customerName,
        customerEmail: addForm.customer?.email || addForm.customerEmail,
        customerPhone: addForm.customer?.phone || addForm.customerPhone,
        licenseNumber: addForm.customerLicense.trim() || null,
        courseLanguage: addIsPrivate ? addForm.courseLanguage : null,
        licenseType: addIsPrivate ? addForm.licenseType : null,
        notes: addForm.notes,
        createdBy: 'admin',
        sendPaymentLink: addForm.sendPaymentLink === true,
      };
      if (payload.sendPaymentLink && !(payload.customerEmail || '').trim() && !(payload.customerPhone || '').trim()) {
        setAddError('Para enviar el link de pago el cliente necesita email o teléfono.');
        setAddSaving(false);
        return;
      }
      const r = await fetch(`${FUNCTIONS_BASE}/adminCreateReservation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (r.ok) {
        // Entrada manual: crear el member si no existe (dedupe por email/teléfono)
        // para que al entrar al app lo detecte y lo obligue a poner su PIN.
        if (!addForm.customer) {
          try {
            await createMemberIfMissing(payload);
          } catch (e) { console.error('No se pudo crear el member:', e); }
        }
        closeAddAttendee();
        fetchCalendarReservations();
        if (data.paymentLink) {
          const pl = data.paymentLink;
          const sent = [pl.emailSent ? 'email' : null, pl.smsSent ? 'SMS' : null].filter(Boolean).join(' y ');
          alert(sent
            ? `Inscripción pendiente de pago. Link enviado por ${sent}.\n\n${pl.url}`
            : `Inscripción pendiente de pago, pero NO se pudo enviar el link (${pl.smsError || pl.emailError || 'sin email ni teléfono'}). Cópialo y envíaselo al cliente:\n\n${pl.url}`);
        } else if (addForm.sendPaymentLink && Number(data.amountCents) === 0) {
          alert('No se envió link de pago: el precio para este cliente es $0 (miembro). La inscripción quedó confirmada.');
        }
      } else {
        setAddError(data.error || 'No se pudo añadir el asistente.');
      }
    } catch (e) {
      setAddError('Error de red.');
    }
    setAddSaving(false);
  };

  // Crea el doc en members si no existe nadie con ese email o teléfono
  const createMemberIfMissing = async ({ customerName, customerEmail, customerPhone, licenseNumber }) => {
    const email = (customerEmail || '').trim().toLowerCase();
    const phone = (customerPhone || '').replace(/\s/g, '');
    if (!email && !phone) return;
    const checks = [];
    if (email) checks.push(getDocs(query(collection(db, 'members'), where('email', '==', email))));
    if (phone) checks.push(getDocs(query(collection(db, 'members'), where('phone', '==', phone))));
    const snaps = await Promise.all(checks);
    if (snaps.some((s) => !s.empty)) return; // ya existe — no duplicar
    await setDoc(doc(collection(db, 'members')), {
      fullName: customerName || '',
      email,
      phone,
      licenseNumber: licenseNumber || '',
      active: false,
      expiration: new Date(),
      createdAt: new Date(),
    });
    fetchMembers();
  };

  const formatPrice = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

  // Eliminar un asistente (borra la reservación)
  const deleteAttendee = async (attendee) => {
    if (!window.confirm(`¿Eliminar a "${attendee.userName || attendee.userEmail}" de esta sesión? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteDoc(doc(db, 'reservations', attendee.id));
      setReservations((prev) => prev.filter((r) => r.id !== attendee.id));
    } catch (e) {
      console.error(e);
      alert('Error eliminando el asistente.');
    }
  };

  // Marcar asistencia: 'completed' (asistió) o 'no_show'. Repetir clic revierte a 'confirmed'.
  // "Asistió" y "Showed" (botón del detalle) son lo mismo (video Lola
  // 2026-10-19): marcar Asistió también enciende showed, y quitarlo / No Show lo
  // apaga, para que el detalle y esta lista digan lo mismo.
  const markAttendance = async (attendee, newStatus) => {
    const attended = attendee.status === 'completed' || attendee.showed === true;
    const status = (newStatus === 'completed' ? attended : attendee.status === newStatus) ? 'confirmed' : newStatus;
    const showed = status === 'completed';
    try {
      await updateDoc(doc(db, 'reservations', attendee.id), { status, showed, showedAt: showed ? new Date() : null, updatedAt: new Date() });
      setReservations((prev) => prev.map((r) => (r.id === attendee.id ? { ...r, status, showed } : r)));
    } catch (e) {
      console.error(e);
      alert('Error actualizando la asistencia.');
    }
  };

  const doPrintAttendance = (slot, dayDate, instructors) => {
    // Si la reservación no tiene licencia, buscarla en el perfil del miembro
    const byEmail = new Map(members.map((m) => [(m.email || '').toLowerCase(), m]));
    const attendees = slot.attendees
      .filter((a) => a.status !== 'no_show')
      .map((a) => {
        if (a.licenseNumber) return a;
        const m = byEmail.get((a.userEmail || '').toLowerCase());
        return { ...a, licenseNumber: (m && (m.licenseNumber || m.gunLicense)) || '' };
      })
      // Sin requisitos del curso → la fila sale en rojo en la hoja (pedido
      // 2026-10-09; antes un modal bloqueaba la impresión).
      .map((a) => ({ ...a, missingReq: !hasRequisito(a) }));
    openAttendanceSheet({
      categoryName: slot.categoryName,
      // Solo los cursos privados marcan "Privado Ing/Esp" con los datos de
      // cada reservación; en los regulares manda el nombre del curso.
      isPrivate: isPrivateCourse(courses.find((c) => c.id === slot.categoryId) || { name: slot.categoryName, type: 'course' }),
      dateISO: toISODate(dayDate),
      startTime: slot.startTime,
      endTime: slot.endTime,
      attendees,
      instructors,
    });
  };

  const sessionMetaId = (slot, dateISO) => `${slot.categoryId}__${dateISO}__${slot.startTime}`;

  // ¿Tiene el asistente al menos un requisito del curso marcado/escrito?
  const hasRequisito = (a) => {
    const cc = a.courseChecks || {};
    return Object.values(cc).some((v) => v === true || (typeof v === 'string' && v.trim() !== ''));
  };

  // Antes de imprimir se releen las reservaciones desde Firestore: si el staff
  // acaba de escribir la licencia / Cal. Balas en el detalle, la lista en
  // memoria de esta página puede estar vieja y la hoja saldría en blanco.
  const refreshSlotAttendees = async (slot) => {
    try {
      const fresh = await Promise.all(slot.attendees.map(async (a) => {
        const snap = await getDoc(doc(db, 'reservations', a.id));
        return snap.exists() ? { id: snap.id, ...snap.data() } : a;
      }));
      // Licencia: si la reservación no la tiene, se toma del perfil del miembro
      // (por email o por teléfono — antes solo por email, y las cuentas por
      // teléfono se quedaban sin licencia en la hoja).
      const byEmail = new Map(members.filter((m) => m.email).map((m) => [(m.email || '').toLowerCase(), m]));
      const byPhone = new Map(members.filter((m) => m.phone).map((m) => [String(m.phone).replace(/\D/g, ''), m]));
      const withLicense = fresh.map((a) => {
        if (a.licenseNumber) return a;
        const m = byEmail.get((a.userEmail || '').toLowerCase())
          || byPhone.get(String(a.userPhone || '').replace(/\D/g, ''));
        return { ...a, licenseNumber: (m && (m.licenseNumber || m.gunLicense)) || '' };
      });
      // Reflejar también en la lista de la página
      setReservations((prev) => prev.map((r) => withLicense.find((f) => f.id === r.id) || r));
      return { ...slot, attendees: withLicense };
    } catch (e) {
      console.error('No se pudieron refrescar los asistentes:', e);
      return slot;
    }
  };

  // Abre el diálogo para marcar instructores; sin empleados, imprime directo.
  const printAttendance = async (staleSlot, dayDate, mode = 'sheet') => {
    const slot = await refreshSlotAttendees(staleSlot);
    // Ya no se bloquea la impresión por requisitos incompletos: la hoja sale
    // igual y esas personas van en rojo (doPrintAttendance).
    if (employees.length === 0 && mode === 'sheet') {
      doPrintAttendance(slot, dayDate, null);
      return;
    }
    const dateISO = toISODate(dayDate);
    const checked = {};
    try {
      const metaSnap = await getDoc(doc(db, 'course_session_meta', sessionMetaId(slot, dateISO)));
      if (metaSnap.exists()) {
        (metaSnap.data().instructorIds || []).forEach((id) => { checked[id] = true; });
      }
    } catch (e) { console.error(e); }
    setPrintMode(mode);
    setPrintChecked(checked);
    setPrintSlot({ slot, dayDate });
    setPrintOpen(true);
  };

  // Certificados: un certificado por asistente con datos del club e instructores
  const doPrintCertificates = async (slot, dayDate) => {
    let club = {};
    try {
      const snap = await getDoc(doc(db, 'settings', 'certificates'));
      if (snap.exists()) {
        club = {
          authorizationNumber: snap.data().clubAuthorizationNumber || '',
          nameLocation: snap.data().clubNameLocation || '',
        };
      }
    } catch (e) { console.error(e); }
    const byEmail = new Map(members.map((m) => [(m.email || '').toLowerCase(), m]));
    const attendees = slot.attendees
      .filter((a) => a.status !== 'no_show')
      .map((a) => {
        if (a.licenseNumber) return a;
        const m = byEmail.get((a.userEmail || '').toLowerCase());
        return { ...a, licenseNumber: (m && (m.licenseNumber || m.gunLicense)) || '' };
      });
    // El formulario PPR-1061 tiene espacio para UN instructor — el primero marcado
    const instructor = employees
      .filter((e) => printChecked[e.id])
      .map((e) => ({ name: e.name, authorizationNumber: e.authorizationNumber || '' }))[0] || null;
    openCertificates({
      categoryName: slot.categoryName,
      dateISO: toISODate(dayDate),
      attendees,
      instructor,
      club,
    });
  };

  const handleConfirmPrint = async () => {
    if (!printSlot) return;
    const { slot, dayDate } = printSlot;
    const dateISO = toISODate(dayDate);
    const instructorIds = employees.filter((e) => printChecked[e.id]).map((e) => e.id);
    setPrintSaving(true);
    try {
      await setDoc(doc(db, 'course_session_meta', sessionMetaId(slot, dateISO)), {
        categoryId: slot.categoryId,
        categoryName: slot.categoryName,
        date: dateISO,
        startTime: slot.startTime,
        instructorIds,
        instructorNames: employees.filter((e) => printChecked[e.id]).map((e) => e.name),
        updatedAt: new Date(),
      }, { merge: true });
    } catch (e) { console.error(e); }
    setPrintSaving(false);
    setPrintOpen(false);
    if (printMode === 'certs') {
      await doPrintCertificates(slot, dayDate);
    } else {
      doPrintAttendance(slot, dayDate, employees.map((e) => ({ name: e.name, checked: !!printChecked[e.id] })));
    }
    setPrintSlot(null);
  };

  // Agrupar por día solo si hay curso programado (rule) o reservación. Días vacíos se omiten.
  const grouped = useMemo(() => {
    const courseById = new Map(courses.map((c) => [c.id, c]));
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      const iso = toISODate(d);
      const dow = d.getDay();

      const dayReservations = reservations
        .filter((r) => r.date === iso)
        .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));

      // Las reglas con `date` son de fecha fija (curso que se da una sola vez)
      // y solo aplican ese día; las demás se repiten por día de la semana.
      const dayRules = scheduleRules
        .filter((r) => r.active !== false)
        .filter((r) => (r.date ? r.date === iso : r.dayOfWeek === dow))
        .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));

      // Empezamos con un slot por cada rule (aunque no tenga asistentes)
      const slotMap = new Map();
      dayRules.forEach((rule) => {
        const cat = courseById.get(rule.categoryId);
        const key = `${rule.categoryId}__${rule.startTime}`;
        slotMap.set(key, {
          key,
          categoryId: rule.categoryId,
          categoryName: cat ? cat.name : 'Curso',
          startTime: rule.startTime,
          endTime: rule.endTime,
          capacity: rule.maxConcurrentBookings || null,
          fromRule: true,
          attendees: [],
        });
      });

      // Añadimos las reservaciones a sus slots o crean nuevos slots si no hay rule
      dayReservations.forEach((r) => {
        const key = `${r.categoryId}__${r.startTime}`;
        if (!slotMap.has(key)) {
          slotMap.set(key, {
            key,
            categoryId: r.categoryId,
            categoryName: r.categoryName,
            startTime: r.startTime,
            endTime: r.endTime,
            capacity: null,
            fromRule: false,
            attendees: [],
          });
        }
        slotMap.get(key).attendees.push(r);
      });

      const slots = Array.from(slotMap.values()).sort(
        (a, b) => (a.startTime || '').localeCompare(b.startTime || '')
      );
      // Asistentes en orden alfabético (pedido del cliente 2026-08-18)
      slots.forEach((s) => {
        s.attendees.sort((a, b) => String(a.userName || a.userEmail || '')
          .localeCompare(String(b.userName || b.userEmail || ''), 'es', { sensitivity: 'base' }));
      });

      return { date: d, iso, slots, dow };
    }).filter((day) => {
      if (day.slots.length === 0) return false;
      // Vista de día: solo el día elegido (aunque sea pasado)
      if (calView === 'day') return day.iso === dayFilter;
      // La semana se muestra COMPLETA, incluidos los días ya pasados. Antes se
      // ocultaban los anteriores a hoy solo en la semana actual, así que los
      // cursos de ayer desaparecían pero sí se veían navegando a la semana
      // pasada — inconsistente y confuso para el empleado.
      return true;
    });
  }, [weekStart, reservations, scheduleRules, courses, calView, dayFilter]);

  const weekLabel = useMemo(() => {
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    return `${weekStart.getDate()} ${MONTHS[weekStart.getMonth()]} – ${end.getDate()} ${MONTHS[end.getMonth()]} ${end.getFullYear()}`;
  }, [weekStart]);

  const navWeek = (delta) => {
    const next = new Date(weekStart);
    next.setDate(weekStart.getDate() + 7 * delta);
    setWeekStart(next);
  };

  const datePickerRef = useRef(null);
  const openDatePicker = () => {
    const el = datePickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try { el.showPicker(); return; } catch (e) { /* fallback */ }
    }
    el.focus();
    el.click();
  };

  const handleDatePickerChange = (e) => {
    if (!e.target.value) return;
    // Saltar a la semana que contiene la fecha elegida
    const picked = new Date(`${e.target.value}T12:00:00`);
    if (Number.isNaN(picked.getTime())) return;
    setWeekStart(startOfWeek(picked));
  };

  return (
    <>
      <Helmet><title>Cursos | BBTC</title></Helmet>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Typography variant="h4">Cursos</Typography>
          {tab === 'list' && (
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={() => navigate('/dashboard/reservation-categories/information?type=course')}
            >
              Nuevo Curso
            </Button>
          )}
        </Stack>

        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab value="list" label="Lista" icon={<Iconify icon="eva:list-fill" />} iconPosition="start" />
          <Tab value="calendar" label="Calendario" icon={<Iconify icon="eva:calendar-fill" />} iconPosition="start" />
        </Tabs>

        {tab === 'list' && (
          <Card>
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <TableBody>
                    {loading && <TableSkeleton rows={5} columns={6} />}
                    {!loading && courses.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          No hay cursos. Crea el primero desde el botón "Nuevo Curso".
                        </TableCell>
                      </TableRow>
                    )}
                    {!loading && courses.length > 0 && (
                      <>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Duración</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Precio Miembro</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Precio No-Miembro</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Activo</TableCell>
                          <TableCell />
                        </TableRow>
                        {courses.map((c) => (
                          <TableRow key={c.id} hover>
                            <TableCell>
                              <Typography variant="subtitle2">{c.name}</Typography>
                              {c.description && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {c.description.slice(0, 80)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{c.durationMinutes} min</TableCell>
                            <TableCell>{formatPrice(c.priceMemberCents)}</TableCell>
                            <TableCell>{formatPrice(c.priceNonMemberCents)}</TableCell>
                            <TableCell>
                              <Switch checked={c.active || false} onChange={() => handleToggleActive(c)} />
                            </TableCell>
                            <TableCell align="right">
                              <IconButton onClick={(e) => handleOpenMenu(e, c)}>
                                <Iconify icon="eva:more-vertical-fill" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          </Card>
        )}

        {tab === 'calendar' && (
          <>
            <Card sx={{ p: 2, mb: 3 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button variant="outlined" size="small" onClick={() => setWeekStart(startOfWeek(new Date()))}>Esta semana</Button>
                  <IconButton size="small" onClick={() => navWeek(-1)}><Iconify icon="eva:chevron-left-fill" /></IconButton>
                  <IconButton size="small" onClick={() => navWeek(1)}><Iconify icon="eva:chevron-right-fill" /></IconButton>
                  <Box
                    onClick={openDatePicker}
                    sx={{
                      ml: 1,
                      position: 'relative',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      cursor: 'pointer',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{weekLabel}</Typography>
                    <Iconify icon="eva:calendar-outline" width={18} sx={{ color: 'text.secondary' }} />
                    <input
                      ref={datePickerRef}
                      type="date"
                      value={toISODate(weekStart)}
                      onChange={handleDatePickerChange}
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: '100%',
                        opacity: 0,
                        pointerEvents: 'none',
                        width: 1,
                        height: 1,
                      }}
                      tabIndex={-1}
                      aria-label="Cambiar semana"
                    />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <ToggleButtonGroup
                    value={calView}
                    exclusive
                    size="small"
                    onChange={(e, v) => {
                      if (!v) return;
                      setCalView(v);
                      if (v === 'day') {
                        const iso = dayFilter || toISODate(new Date());
                        setDayFilter(iso);
                        setWeekStart(startOfWeek(new Date(`${iso}T12:00:00`)));
                      }
                    }}
                  >
                    <ToggleButton value="week">Semana</ToggleButton>
                    <ToggleButton value="day">Día</ToggleButton>
                  </ToggleButtonGroup>
                  {calView === 'day' && (
                    <TextField
                      size="small"
                      type="date"
                      label="Día"
                      value={dayFilter}
                      onChange={(e) => {
                        if (!e.target.value) return;
                        setDayFilter(e.target.value);
                        setWeekStart(startOfWeek(new Date(`${e.target.value}T12:00:00`)));
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  <TextField
                    select
                    size="small"
                    label="Filtrar curso"
                    value={filterCourseId}
                    onChange={(e) => setFilterCourseId(e.target.value)}
                    sx={{ minWidth: 220 }}
                  >
                    <MenuItem value="">Todos los cursos</MenuItem>
                    {courses.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                  </TextField>
                </Stack>
              </Stack>
            </Card>

            {calLoading ? (
              <Stack spacing={2}>
                {[1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={80} sx={{ borderRadius: 2 }} />)}
              </Stack>
            ) : grouped.length === 0 ? (
              <Card>
                <CardContent>
                  <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                    <Iconify icon="eva:calendar-outline" width={48} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      No hay cursos programados esta semana
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                      Configura los días y horas en cada curso desde "Categorías → Editar".
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ) : (
              <Stack spacing={2}>
                {grouped.map((day) => {
                  const isToday = toISODate(new Date()) === day.iso;
                  return (
                    <Card key={day.iso}>
                      {/* Toda la tarjeta abre/cierra; las sesiones adentro no propagan el clic */}
                      <CardContent
                        onClick={() => toggleDayExpanded(day.iso)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={2}
                          sx={{ mb: day.slots.length ? 2 : 0 }}
                        >
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: 2,
                              bgcolor: isToday ? 'primary.main' : 'action.hover',
                              color: isToday ? 'primary.contrastText' : 'text.primary',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1 }}>
                              {MONTHS[day.date.getMonth()].slice(0, 3).toUpperCase()}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                              {day.date.getDate()}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {DAYS_FULL[day.date.getDay()]}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {day.slots.length === 0
                                ? 'Sin reservaciones'
                                : `${day.slots.length} ${day.slots.length === 1 ? 'sesión' : 'sesiones'} · ${day.slots.reduce((sum, s) => sum + s.attendees.length, 0)} asistentes`}
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleDayExpanded(day.iso); }}>
                            <Iconify
                              icon="eva:chevron-down-fill"
                              width={22}
                              sx={{
                                transform: expandedDays[day.iso] ? 'rotate(180deg)' : 'none',
                                transition: 'transform 0.2s',
                              }}
                            />
                          </IconButton>
                        </Stack>

                        {day.slots.length > 0 && (
                          <Stack spacing={2} onClick={(e) => e.stopPropagation()} sx={{ cursor: 'default' }}>
                            {day.slots.map((slot) => (
                              <Card key={slot.key} variant="outlined">
                                <CardContent>
                                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                      <Iconify icon="eva:clock-fill" />
                                      <Typography variant="subtitle2">
                                        {formatTime(slot.startTime)} – {formatTime(slot.endTime)}
                                      </Typography>
                                      <Chip label={slot.categoryName} size="small" color="primary" />
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                      <Chip
                                        label={
                                          slot.capacity
                                            ? `${slot.attendees.length}/${slot.capacity}`
                                            : `${slot.attendees.length} ${slot.attendees.length === 1 ? 'asistente' : 'asistentes'}`
                                        }
                                        color={slot.attendees.length > 0 ? 'success' : 'default'}
                                        size="small"
                                      />
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Iconify icon="eva:list-fill" />}
                                        onClick={() => printAttendance(slot, day.date)}
                                        disabled={slot.attendees.filter((a) => a.status !== 'no_show').length === 0}
                                      >
                                        Lista
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<Iconify icon="eva:award-fill" />}
                                        onClick={() => printAttendance(slot, day.date, 'certs')}
                                        disabled={slot.attendees.filter((a) => a.status !== 'no_show').length === 0}
                                      >
                                        Certificado
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<Iconify icon="eva:plus-fill" />}
                                        onClick={() => openAddAttendee(slot, day.date)}
                                      >
                                        Añadir asistente
                                      </Button>
                                    </Stack>
                                  </Stack>
                                  {/* Lista de asistentes: oculta hasta expandir el día */}
                                  <Collapse in={!!expandedDays[day.iso]} timeout="auto" unmountOnExit>
                                  <Divider sx={{ mb: 1.5 }} />
                                  {slot.attendees.length === 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                      Sin asistentes registrados aún.
                                    </Typography>
                                  )}
                                  <Stack spacing={1}>
                                    {slot.attendees.map((a) => (
                                      <Stack
                                        key={a.id}
                                        direction="row"
                                        alignItems="center"
                                        spacing={2}
                                        onClick={() => goToAttendee(a.id)}
                                        sx={{
                                          cursor: 'pointer',
                                          p: 1,
                                          borderRadius: 1,
                                          transition: 'background-color 0.15s',
                                          '&:hover': { bgcolor: 'action.hover' },
                                        }}
                                      >
                                        <Avatar sx={{ width: 32, height: 32, fontSize: 13 }}>
                                          {(a.userName || '?').charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {a.userName || a.userEmail}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {a.userEmail}{a.userPhone ? ` · ${a.userPhone}` : ''}
                                          </Typography>
                                          {privateCourseSummary(a, courses.find((c) => c.id === slot.categoryId)) && (
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'error.main' }}>
                                              {privateCourseSummary(a, courses.find((c) => c.id === slot.categoryId))}
                                            </Typography>
                                          )}
                                          {/* Aliado: el curso lo cobró la armería. En mostrador solo
                                              se cobra renta de arma y ammo con licencia nueva o con la
                                              licencia vencida (V3, 2026-09-24). */}
                                          {a.partnerName && (
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'warning.dark' }}>
                                              Aliado: {a.partnerName} (no paga el curso en mostrador{partnerRentalReason(a) === 'new' ? '; cobrar renta de arma y ammo' : ''})
                                            </Typography>
                                          )}
                                          {a.partnerName && partnerRentalReason(a) === 'expired' && (
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'error.main' }}>
                                              Licencia vencida: cobrar renta de arma y ammo
                                            </Typography>
                                          )}
                                        </Box>
                                        {/* "Showed" marcado en el detalle → verde aquí también,
                                            igual que "Asistió" (pedido 2026-10-07). */}
                                        {a.showed && a.status !== 'no_show' ? (
                                          <Chip
                                            label={a.status === 'completed' ? 'Asistió · Showed' : 'Showed'}
                                            size="small"
                                            color="success"
                                            icon={<Iconify icon="eva:person-done-fill" />}
                                          />
                                        ) : (
                                          <Chip
                                            label={a.status === 'pending_payment' ? 'Pendiente' : a.status === 'completed' ? 'Asistió' : a.status === 'no_show' ? 'No Show' : 'Confirmada'}
                                            size="small"
                                            color={a.status === 'pending_payment' ? 'warning' : a.status === 'completed' ? 'success' : a.status === 'no_show' ? 'error' : 'info'}
                                            variant="outlined"
                                          />
                                        )}
                                        <Button
                                          size="small"
                                          variant={a.status === 'completed' || (a.showed && a.status !== 'no_show') ? 'contained' : 'outlined'}
                                          color="success"
                                          startIcon={<Iconify icon="eva:checkmark-fill" />}
                                          onClick={(e) => { e.stopPropagation(); markAttendance(a, 'completed'); }}
                                          sx={{ minWidth: 92 }}
                                        >
                                          Asistió
                                        </Button>
                                        <Button
                                          size="small"
                                          variant={a.status === 'no_show' ? 'contained' : 'outlined'}
                                          color="error"
                                          startIcon={<Iconify icon="eva:close-fill" />}
                                          onClick={(e) => { e.stopPropagation(); markAttendance(a, 'no_show'); }}
                                          sx={{ minWidth: 100 }}
                                        >
                                          No Show
                                        </Button>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={(e) => { e.stopPropagation(); deleteAttendee(a); }}
                                          title="Eliminar asistente"
                                        >
                                          <Iconify icon="eva:trash-2-outline" width={18} />
                                        </IconButton>
                                        <Iconify icon="eva:chevron-right-fill" width={18} sx={{ color: 'text.secondary' }} />
                                      </Stack>
                                    ))}
                                  </Stack>
                                  </Collapse>
                                </CardContent>
                              </Card>
                            ))}
                          </Stack>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </>
        )}
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 1, width: 140 } }}
      >
        <MenuItem onClick={handleEdit}>
          <Iconify icon="eva:edit-fill" sx={{ mr: 2 }} /> Editar
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <Iconify icon="eva:trash-2-outline" sx={{ mr: 2 }} /> Eliminar
        </MenuItem>
      </Popover>

      {/* Print: selección de instructores */}
      <Dialog open={printOpen} onClose={() => setPrintOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>¿Quién dio el curso?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {printMode === 'certs'
              ? 'Marca el/los instructores que firman los certificados (salen con su número de autorización).'
              : 'Marca el/los instructores. Salen impresos en la hoja con su checkmark; los no marcados salen con el cuadrito en blanco para marcarlos a mano.'}
          </Typography>
          <Stack>
            {employees.length === 0 && (
              <Alert severity="warning">
                No hay empleados con rol de <b>Instructor</b>. Asigna el rol (y su
                número de autorización) en <b>Empleados → editar</b>.
              </Alert>
            )}
            {employees.map((e) => (
              <FormControlLabel
                key={e.id}
                control={
                  <Checkbox
                    checked={!!printChecked[e.id]}
                    onChange={(ev) => setPrintChecked({ ...printChecked, [e.id]: ev.target.checked })}
                  />
                }
                label={e.name}
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleConfirmPrint}
            disabled={printSaving}
            startIcon={printSaving ? <CircularProgress size={16} /> : <Iconify icon="eva:printer-fill" />}
          >
            {printMode === 'certs' ? 'Imprimir certificados' : 'Imprimir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Attendee Dialog */}
      <Dialog open={addOpen} onClose={closeAddAttendee} maxWidth="sm" fullWidth>
        <DialogTitle>Añadir asistente</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {addSlot && (
              <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {addSlot.categoryName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {addSlot.date} · {formatTime(addSlot.startTime)} – {formatTime(addSlot.endTime)}
                </Typography>
              </Box>
            )}

            {addError && <Alert severity="error">{addError}</Alert>}

            <Autocomplete
              options={members}
              openOnFocus
              getOptionLabel={(opt) => opt.fullName ? `${opt.fullName} — ${opt.email || opt.phone || ''}` : ''}
              value={addForm.customer}
              onChange={(e, v) => setAddForm({
                ...addForm,
                customer: v,
                customerName: v ? (v.fullName || '') : '',
                customerEmail: v ? (v.email || '') : '',
                customerPhone: v ? (v.phone || '') : '',
                customerLicense: v ? (v.licenseNumber || v.gunLicense || '') : '',
              })}
              renderInput={(params) => <TextField {...params} label="Buscar cliente existente" placeholder="Nombre, email o teléfono" />}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              filterOptions={(options, state) => {
                // Ignora acentos y el orden de las palabras: "jose cruz"
                // encuentra "José E. Rodríguez Cruz" (pedido del cliente)
                const q = state.inputValue;
                if (!q.trim()) return options.slice(0, 50);
                return options.filter((o) => matchesSearch(q, [
                  o.fullName, o.email, o.phone, o.memberId,
                ])).slice(0, 50);
              }}
            />

            <Typography variant="caption" color="text.secondary">
              O ingresa los datos manualmente:
            </Typography>

            <TextField
              label="Nombre"
              value={addForm.customerName}
              onChange={(e) => setAddForm({ ...addForm, customerName: e.target.value })}
              fullWidth
              disabled={!!addForm.customer}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Email"
                value={addForm.customerEmail}
                onChange={(e) => setAddForm({ ...addForm, customerEmail: e.target.value })}
                fullWidth
                disabled={!!addForm.customer}
              />
              <TextField
                label="Teléfono"
                value={addForm.customerPhone}
                onChange={(e) => setAddForm({ ...addForm, customerPhone: e.target.value })}
                fullWidth
                disabled={!!addForm.customer}
              />
            </Stack>
            <TextField
              label="# Licencia (opcional)"
              value={addForm.customerLicense}
              onChange={(e) => setAddForm({ ...addForm, customerLicense: e.target.value })}
              fullWidth
              helperText="Se pre-llena en la hoja de asistencia impresa."
            />
            {addSlot && isPrivateCourse(courses.find((c) => c.id === addSlot.categoryId) || { name: addSlot.categoryName, type: 'course' }) && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Curso privado *</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Idioma y si es licencia nueva o renovación (define el pago al instructor).
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                  {COURSE_LANGUAGE_OPTIONS.map((o) => (
                    <Chip
                      key={o.value}
                      label={o.label}
                      clickable
                      onClick={() => setAddForm((prev) => ({ ...prev, courseLanguage: o.value }))}
                      color={addForm.courseLanguage === o.value ? 'error' : 'default'}
                      variant={addForm.courseLanguage === o.value ? 'filled' : 'outlined'}
                      sx={{ fontWeight: 600 }}
                    />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {LICENSE_TYPE_OPTIONS.map((o) => (
                    <Chip
                      key={o.value}
                      label={o.label}
                      clickable
                      onClick={() => setAddForm((prev) => ({ ...prev, licenseType: o.value }))}
                      color={addForm.licenseType === o.value ? 'error' : 'default'}
                      variant={addForm.licenseType === o.value ? 'filled' : 'outlined'}
                      sx={{ fontWeight: 600 }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            <TextField
              label="Notas (opcional)"
              value={addForm.notes}
              onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <Box sx={{ p: 1.5, border: '1px dashed', borderColor: addForm.sendPaymentLink ? 'warning.main' : 'divider', borderRadius: 2 }}>
              <FormControlLabel
                control={<Switch checked={addForm.sendPaymentLink === true} onChange={(e) => setAddForm({ ...addForm, sendPaymentLink: e.target.checked })} color="warning" />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Enviar link de pago por SMS y email</Typography>}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 0.5 }}>
                {addForm.sendPaymentLink
                  ? 'La inscripción queda PENDIENTE hasta que el cliente pague por el link (precio de miembro o no-miembro + IVU). Al pagar, se confirma sola.'
                  : 'Apagado: la inscripción queda confirmada y el pago se coordina en mostrador.'}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddAttendee}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAddAttendee}
            disabled={addSaving}
            startIcon={addSaving ? <CircularProgress size={16} /> : null}
          >
            {addSaving ? 'Añadiendo...' : 'Añadir asistente'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
